import { APP_LOCAL_DATE_FORMAT } from "@/config/constants";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { IEdoUnit } from "@/shared/model/edo-unit.model";
import { getFileAvatar } from "@/shared/util/file-avatar-utils";
import {
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@/components/ui";
import { Bookmark, ChevronDown, ChevronUp, Download, Eye } from "lucide-react";
import axios from "axios";
import "moment/locale/it";
import React, { useEffect, useState } from "react";
import { TextFormat } from "react-jhipster";

interface UnitRowProps {
  unit?: IEdoUnit;
  isLastElement?: boolean;
}

export const UnitRow = (props: UnitRowProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [files, setFiles] = useState<IEdoFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);

  const { unit, isLastElement } = props;

  const handleOpen = () => {
    setOpen(!open);
  };

  const openFile = (file: IEdoFile) => {
    window.open(`/api/files/${file.id}/view`, "_blank");
  };

  const downloadFile = (file: IEdoFile) => {
    window.open(`/api/files/${file.id}/download`, "_blank");
  };

  useEffect(() => {
    const loadFiles = async (unitItem: IEdoUnit) => {
      setLoadingFiles(true);
      const response = await axios.get(`/units/${unitItem.id}/files`);
      setFiles(response.data.data);
      setLoadingFiles(false);
    };

    if (unit && open) {
      loadFiles(unit);
    }
  }, [unit, open]);

  useEffect(() => {
    if (!unit || loadingFiles) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [unit, loadingFiles]);

  const getContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-4">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      );
    }

    if (!files.length) {
      return (
        <div className="p-4 text-center">
          <p className="text-body text-[var(--color-mockup-text-mid)]">
            Nessun materiale contenuto nell'unità
          </p>
        </div>
      );
    }

    return (
      <List className="border-t border-[var(--color-mockup-border)] pl-4 dark:border-[var(--color-mockup-border)] sm:pl-6">
        {files.map((file) => (
          <ListItem
            key={file.id}
            className="flex cursor-pointer items-center gap-3 py-2 hover:bg-primary/5"
            onClick={() => openFile(file)}
          >
            <ListItemIcon className="shrink-0">
              {getFileAvatar(file.fileType)}
            </ListItemIcon>
            <ListItemText
              primary={file.originalFileName}
              secondary={
                <TextFormat
                  value={file.createdAt}
                  type="date"
                  format={APP_LOCAL_DATE_FORMAT}
                />
              }
              className="min-w-0 flex-1 truncate text-sm"
            />
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                openFile(file);
              }}
              aria-label="Apri file"
            >
              <Eye className="h-4 w-4" />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                downloadFile(file);
              }}
              aria-label="Scarica file"
            >
              <Download className="h-4 w-4" />
            </IconButton>
          </ListItem>
        ))}
      </List>
    );
  };

  if (!unit) return null;

  return (
    <>
      <ListItem
        className="flex cursor-pointer items-center gap-3 py-3 hover:bg-primary/5"
        onClick={handleOpen}
      >
        <ListItemIcon className="shrink-0">
          <Avatar className="h-9 w-9 bg-primary/10">
            <Bookmark className="h-4 w-4 text-primary" />
          </Avatar>
        </ListItemIcon>
        <ListItemText
          primary={unit.title}
          secondary={
            <TextFormat
              value={unit.createdAt}
              type="date"
              format={APP_LOCAL_DATE_FORMAT}
            />
          }
          className="min-w-0 flex-1 truncate"
        />
        {open ? (
          <ChevronUp className="h-5 w-5 shrink-0 text-[var(--color-mockup-text-light)]" />
        ) : (
          <ChevronDown className="h-5 w-5 shrink-0 text-[var(--color-mockup-text-light)]" />
        )}
      </ListItem>
      {open && (
        <div className="border-t border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30">
          {getContent()}
          {!isLastElement && (
            <div className="border-t border-[var(--color-mockup-border)]" />
          )}
        </div>
      )}
    </>
  );
};
